import type * as SentrySdk from "@sentry/nextjs";
import {
  browserSentryOptions,
  isSentryEnabledInBrowser,
} from "@/lib/sentry/config";

type Sdk = typeof SentrySdk;

let sdkPromise: Promise<Sdk> | null = null;

/**
 * Downloads @sentry/nextjs and initializes it once with the shared browser
 * options. The SDK is ~90 KB gzip, so it is never part of the JS that blocks
 * the first render; instrumentation-client decides when loading starts.
 * Returns null where browser Sentry is off (localhost).
 */
export function loadBrowserSentry(): Promise<Sdk> | null {
  if (!isSentryEnabledInBrowser()) return null;
  sdkPromise ??= import("@sentry/nextjs").then((sdk) => {
    sdk.init(browserSentryOptions());
    return sdk;
  });
  return sdkPromise;
}

/**
 * Runs `use` once the SDK is initialized. A failed load can only be logged:
 * the thing that failed is the error reporter itself.
 */
export function withBrowserSentry(use: (sdk: Sdk) => void): void {
  loadBrowserSentry()
    ?.then(use)
    .catch((error: unknown) => {
      console.error("Failed to load Sentry:", error);
    });
}
